import { IModule, IRole } from 'features/auth/login/LoginScreen.type';
import { ISalesFilterParams } from 'features/industrySales/IndustrySales.type';
import isEmpty from 'lodash/isEmpty';
import moment, { Moment } from 'moment';
import { createTextColumn } from 'react-datasheet-grid';
import { FUEL_TYPE_OPTION, SEGMENT_OPTION, SUB_SEGMENT_OPTION } from 'shared/constants/constant';
import { IDropDownList, IDropDownOption } from 'shared/interface';
import { IAction } from 'shared/interface/state';
import AuthService from 'shared/services/Auth.service';

/**
 * create action creator
 * @param ACTION - type of action
 * @param data - data
 */
export const createAction = (ACTION: string, data: any = null): IAction => ({
	type: ACTION,
	payload: data
});

export const checkPermission = (module: string, permission: string) => {
	const userData = AuthService.getAuthData();
	const currentModule: IModule[] =
		(userData && userData.role.modules.filter((item: IModule) => item.module_name === module)) || [];
	// Check if the user has the required permission
	const hasRequiredPermission =
		!isEmpty(currentModule) && (currentModule[0] as IModule).permissions.includes(permission);
	return hasRequiredPermission;
};

export const checkTimeline = () => {
	const userData = AuthService.getAuthData();
	const isTimelineStarted = moment().isAfter(moment(userData?.sales_start_date));
	const isTimelineEnded = moment().isAfter(moment(userData?.sales_end_date));
	const hasBetweenTimeline = isTimelineStarted && !isTimelineEnded;
	return hasBetweenTimeline;
};

export const dateFormate = (date: any) => {
	if (!date) {
		return '';
	}
	return moment(date).local().format('DD-MM-YYYY | HH:mm:ss');
};

export const calculateDayDifference = (date: string, specificDate: string) => {
	// Convert both input parameters to moment objects if they are not already
	const startDate: Moment = moment(date);
	const endDate: Moment = moment(specificDate);

	// Calculate the day difference
	const dayDifference: number = endDate.diff(startDate, 'days') + 1;

	return `${dayDifference} ${dayDifference === 1 ? 'Day' : 'Days'}` as string;
};

export const generateColumns = (columns: any[], role: IRole) => {
	return columns.filter((column) => role.master_fields.includes(column.id));
};

export const formatSegmentFieldLabel = (label: string) => {
	// Replace underscores with - and capitalize the first letter
	return label.replace(/_/g, '-').replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
	});
};

export const formatFieldLabel = (label: string) => {
	// Replace underscores with spaces and capitalize the first letter
	return label.replace(/_/g, ' ').replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
	});
};

export const formatFieldError = (label: string) => {
	return label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' ');
};

export const getMenuItemForField = (field: string, dropdownList: IDropDownList) => {
	if (field === 'segment') {
		return SEGMENT_OPTION;
	}
	if (field === 'sub_segment') {
		return SUB_SEGMENT_OPTION;
	}
	if (field === 'fuel_type') {
		return FUEL_TYPE_OPTION;
	}
	// Default menuItem for other fields
	return (dropdownList[field] as IDropDownOption[]) || [];
};

export const boxesInColumn = (filterFields: string[]) => {
	const selectBoxesInColumns = [];
	for (let i = 0; i < filterFields.length; i += 2) {
		const column = filterFields.slice(i, i + 2);
		selectBoxesInColumns.push(column);
	}
	return selectBoxesInColumns;
};

export const zeroIntColumn = createTextColumn<number | null>({
	alignRight: true,
	formatBlurredInput: (value) => (typeof value === 'number' ? value.toString() : ''),
	parseUserInput: (value) => {
		const number = parseFloat(value);
		return !isNaN(number) ? Math.round(number) : 0;
	},
	parsePastedValue: (value) => {
		const number = parseFloat(value);
		return !isNaN(number) ? Math.round(number) : 0;
	},
	deletedValue: 0
});

export const monthColumn = createTextColumn<number | null>({
	formatBlurredInput: (value) => (typeof value === 'number' ? moment(value, 'M').format('MMMM') : '')
});

export const downloadedFileName = (pdfName: string, filterParams: ISalesFilterParams) => {
	const { sales_year, sales_month, manufacturer, brand } = filterParams;
	const monthName = sales_month && moment.months(sales_month - 1).toLocaleLowerCase();
	const manuFacturerName = manufacturer && manufacturer.replace(/ /g, '-');
	const brandName = brand && brand.replace(/ /g, '-');
	const nameParts = [sales_year, monthName, manuFacturerName, brandName].filter(Boolean);

	if (nameParts.length > 0) {
		pdfName = `${nameParts.join('-')}-${pdfName}`;
	}
	return pdfName;
};

export const mergeInteractions = (like: any[], dislike: any[]) => {
	return [
		...like.map((item) => ({
			name: item.name,
			interaction: 'like', // Mark as "like"
			interaction_at: item.interaction_at
		})),
		...dislike.map((item) => ({
			name: item.name,
			interaction: 'dislike', // Mark as "dislike"
			interaction_at: item.interaction_at
		}))
	];
};
